import { useCallback, useEffect, useRef } from 'react';

import { FASTSHIFT_ASSESSMENT_RISKS } from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';

import { ActionKey, ActionSubjectIdKey, ActionSubjectKey } from '@/analytics/constants';
import { useFastshiftAnalyticsData } from '@/analytics/hooks/use-fastshift-analytics-data';
import { useFireAnalyticsEvents } from '@/analytics/use-fire-analytics-events';
import type { DcInstanceDetail } from '@/state/types';

import type { AssessmentResultsStatusFilterValue } from '../src/assessment-results-section/assessment-results-status-filter';

type DcInstanceDetailsPageAnalyticsProps = {
	organizationId: string;
	details: Pick<DcInstanceDetail, 'pending' | 'error' | 'data'>;
};

export type RecommendationDetailsEntryPoint = 'topRecommendation' | 'assessmentResult';

const getNeedsAttentionCount = (details: NonNullable<DcInstanceDetail['data']>) =>
	details.assessment.results.filter(
		({ impact }) =>
			impact?.status === FASTSHIFT_ASSESSMENT_RISKS.MEDIUM_RISK ||
			impact?.status === FASTSHIFT_ASSESSMENT_RISKS.HIGH_RISK,
	).length;

export const useDcInstanceDetailsPageAnalytics = ({
	organizationId,
	details: { pending, error, data },
}: DcInstanceDetailsPageAnalyticsProps) => {
	const { sourceKey, identityAttrs, uiIdentityAttrs } = useFastshiftAnalyticsData();
	const { fireScreenEvent, fireTrackEvent } = useFireAnalyticsEvents(identityAttrs);
	const { fireUIEvent } = useFireAnalyticsEvents(uiIdentityAttrs);
	const displayedStatus = pending ? null : error ? 'error' : data ? 'normal' : null;
	const assessmentStatus = data?.assessment.assessmentStatus ?? null;
	const needsAttentionCount = data ? getNeedsAttentionCount(data) : null;
	const lastFiredPageDetailsKeyRef = useRef<string | null>(null);

	useEffect(() => {
		if (identityAttrs === null) {
			return;
		}

		fireScreenEvent({
			name: sourceKey,
			attributes: { selectedOrgId: organizationId },
		});
	}, [fireScreenEvent, identityAttrs, organizationId, sourceKey]);

	useEffect(() => {
		if (identityAttrs === null || displayedStatus === null) {
			return;
		}

		const attributes =
			displayedStatus === 'error'
				? { selectedOrgId: organizationId, status: displayedStatus }
				: assessmentStatus === null || needsAttentionCount === null
					? null
					: {
							selectedOrgId: organizationId,
							status: displayedStatus,
							assessmentStatus,
							needsAttentionCount,
						};

		if (attributes === null) {
			return;
		}

		const eventKey = JSON.stringify({ ...identityAttrs, ...attributes });
		if (lastFiredPageDetailsKeyRef.current === eventKey) {
			return;
		}

		fireTrackEvent({
			action: ActionKey.DISPLAYED,
			actionSubject: ActionSubjectKey.SECTION,
			actionSubjectId: ActionSubjectIdKey.DC_INSTANCE_DETAILS_PAGE_DETAILS,
			source: sourceKey,
			attributes,
		});
		lastFiredPageDetailsKeyRef.current = eventKey;
	}, [
		assessmentStatus,
		displayedStatus,
		fireTrackEvent,
		identityAttrs,
		needsAttentionCount,
		organizationId,
		sourceKey,
	]);

	const sendAssessmentStatusFilterClickUIEvent = useCallback(
		(selectedAssessmentFilter: AssessmentResultsStatusFilterValue) => {
			fireUIEvent({
				action: ActionKey.CLICKED,
				actionSubject: ActionSubjectKey.DROPDOWN_ITEM,
				actionSubjectId: ActionSubjectIdKey.DC_INSTANCE_ASSESSMENT_STATUS_FILTER_DROPDOWN_ITEM,
				source: sourceKey,
				attributes: { selectedOrgId: organizationId, selectedAssessmentFilter },
			});
		},
		[fireUIEvent, organizationId, sourceKey],
	);

	const sendAssessmentClearFiltersClickUIEvent = useCallback(() => {
		fireUIEvent({
			action: ActionKey.CLICKED,
			actionSubject: ActionSubjectKey.BUTTON,
			actionSubjectId: ActionSubjectIdKey.DC_INSTANCE_ASSESSMENT_CLEAR_FILTERS_BUTTON,
			source: sourceKey,
			attributes: { selectedOrgId: organizationId },
		});
	}, [fireUIEvent, organizationId, sourceKey]);

	const sendAssessmentFilterChangeUIEvent = useCallback(
		(filter: AssessmentResultsStatusFilterValue | null) => {
			if (filter === null) {
				sendAssessmentClearFiltersClickUIEvent();
				return;
			}

			sendAssessmentStatusFilterClickUIEvent(filter);
		},
		[sendAssessmentClearFiltersClickUIEvent, sendAssessmentStatusFilterClickUIEvent],
	);

	const sendRecommendationDetailsClickUIEvent = useCallback(
		(assessmentResultId: string, entryPoint: RecommendationDetailsEntryPoint) => {
			fireUIEvent({
				action: ActionKey.CLICKED,
				actionSubject: ActionSubjectKey.BUTTON,
				actionSubjectId: ActionSubjectIdKey.DC_INSTANCE_RECOMMENDATION_DETAILS_BUTTON,
				source: sourceKey,
				attributes: { selectedOrgId: organizationId, assessmentResultId, entryPoint },
			});
		},
		[fireUIEvent, organizationId, sourceKey],
	);

	return { sendAssessmentFilterChangeUIEvent, sendRecommendationDetailsClickUIEvent };
};
